import React, { useState, useEffect } from 'react';
import axiosInstance from '../../services/axiosConfig';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { authService } from '../../services/auth.service';
import { io } from 'socket.io-client';
import BranchSelector from '../BranchSelector';
import { goToTrainingOrLogin } from '../../constants/training';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const reduxUser = useSelector((state: RootState) => state.auth.user);
  const [user, setUser] = useState(reduxUser);

  useEffect(() => {
    if (!reduxUser) {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    } else {
      setUser(reduxUser);
    }
  }, [reduxUser]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
    handleClose();
  };

  const handleSettings = () => {
    navigate('/settings');
    handleClose();
  };

  const handleProfile = () => {
    // Placeholder for profile page navigation
    handleClose();
  };

  const handleHelp = () => {
    goToTrainingOrLogin(navigate);
    handleClose();
  };

  // Compute display name for user
  const displayName = user?.full_name || user?.name || user?.email || 'User';

  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const isNotifOpen = Boolean(notifAnchorEl);

  const isStaffOrAdmin = user && (user.role === 'Admin' || user.role === 'SuperAdmin' || user.role === 'Staff');

  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axiosInstance.get('/notifications');
      const data = res.data;
      if (Array.isArray(data)) {
        setNotifications(data);
        setUnreadCount(data.filter((n: any) => !n.read).length);
      } else {
        setNotifications([]);
        setUnreadCount(0);
        // Optionally handle error: show a message, log, etc.
      }
    } catch (err) {
      setNotifications([]);
      setUnreadCount(0);
      // Optionally handle error
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    if (isStaffOrAdmin) fetchNotifications();
    const enableSocket = process.env.REACT_APP_ENABLE_SOCKETIO === 'true';
    if (!enableSocket) {
      return; // Socket.IO disabled in production by default
    }
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || '';
    const socket = io(baseUrl, { transports: ['websocket'] });
    socket.on('new-notification', (notification) => {
      setNotifications((prev) => [notification, ...prev]);
      setUnreadCount((prev) => prev + 1);
    });
    return () => {
      socket.disconnect();
    };
  }, [isStaffOrAdmin]);

  const handleNotifClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(event.currentTarget);
    fetchNotifications();
  };
  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };
  const handleNotifItemClick = async (notif: any) => {
    if (!notif.read) {
      try {
        await axiosInstance.patch(`/notifications/${notif.id}/read`);
        // Update UI immediately
        setNotifications((prev) => prev.map(n => n.id === notif.id ? { ...n, read: 1 } : n));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {

      }
    }
    // Navigate to order if order_id exists
    if (notif.order_id) {
      navigate(`/orders/${notif.order_id}`);
      handleNotifClose();
    }
  };

  const handleMarkAllRead = async () => {
    const token = localStorage.getItem('token');
    await axiosInstance.patch('/notifications/mark-all-read');
    setNotifications((prev) => prev.map(n => ({ ...n, read: 1 })));
    setUnreadCount(0);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={onMenuClick}
          edge="start"
          sx={{
            marginRight: 2,
            ...(isMobile && {
              marginRight: 0,
            }),
          }}
        >
          <MenuIcon />
        </IconButton>
        
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }}
        >
          TailorHub
        </Typography>
        {/* Global Branch Selector for Admin/SuperAdmin */}
        {(user?.role === 'Admin' || user?.role === 'SuperAdmin') && (
          <Box sx={{ mr: 2 }}>
            <BranchSelector showCurrency={true} />
          </Box>
        )}
        {/* Show logged-in user's name in the header */}
          <Typography
            variant="subtitle1"
            sx={{ color: 'white', fontWeight: 500, mr: 2, display: { xs: 'none', sm: 'block' } }}
            noWrap
          >
          {displayName}
          </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Help Button */}
          <IconButton
            color="inherit"
            onClick={handleHelp}
            sx={{ mr: 1 }}
            size="large"
            title="Help & Training - Access system tutorials and support resources"
          >
            <HelpIcon />
          </IconButton>
          
          {isStaffOrAdmin && (
            <>
              <IconButton color="inherit" onClick={handleNotifClick}>
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <Popover
                open={isNotifOpen}
                anchorEl={notifAnchorEl}
                onClose={handleNotifClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { minWidth: 320, maxWidth: 400 } }}
              >
                <Box sx={{ p: 2, minWidth: 300 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>Notifications</Typography>
                    {unreadCount > 0 && (
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{ cursor: 'pointer', fontWeight: 500 }}
                        onClick={handleMarkAllRead}
                      >
                        Mark all as read
                      </Typography>
                    )}
                  </Box>
                  {notifLoading ? (
                    <CircularProgress size={24} />
                  ) : notifications.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No notifications</Typography>
                  ) : (
                    <List>
                      {notifications.slice(0, 10).map((notif) => (
                        <ListItem
                          key={notif.id}
                          onClick={() => handleNotifItemClick(notif)}
                          sx={{
                            background: !notif.read ? 'rgba(25, 118, 210, 0.08)' : 'rgba(0,0,0,0.04)',
                            mb: 0.5,
                            borderRadius: 1,
                            cursor: 'pointer'
                          }}
                        >
                          <ListItemText
                            primary={notif.message}
                            secondary={notif.created_at ? new Date(notif.created_at).toLocaleString() : ''}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>
              </Popover>
            </>
          )}
          <IconButton
            color="inherit"
            onClick={handleMenu}
            sx={{ ml: 1 }}
            size="large"
          >
            <Avatar>{displayName.charAt(0).toUpperCase()}</Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {displayName}
              </Typography>
              {user?.email && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {user.email}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.role && user.role.charAt(0).toUpperCase() + user.role.slice(1)}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleSettings}>Settings</MenuItem>
            <MenuItem onClick={handleProfile}>Profile</MenuItem>
            <MenuItem onClick={handleHelp}>Help</MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ display: { xs: 'flex', md: 'none' } }}>Logout</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header; 